import { UIIcon } from '../../../config/icons';
import { $on } from '../../y/events';
import { $i18n } from '../../y/i18n';
import { yResolve } from '../../y/util';
import { $el } from '../elements';
import { UIMenu, UIMenuItem, uiShowMenu } from '../menu';
import { uiShowPopup } from '../popup';
import { uiAddRects } from '../rect';
import { UIClassesSpec, UIRect, UIRelPos, UIValue } from '../types';
import { UIView, UIViewSpec } from '../view';
import { UIButton } from './button';
import { UISelect } from './select';

export interface UIMenuHeaderOptions {
    position?: UIRelPos;
    no_crumbs?: boolean;
    button_classes?: UIClassesSpec;
}

interface UIMenuBarOptions {
    kind?: 'primary' | 'secondary' | 'inline';
    classes?: UIClassesSpec;
}

function makeMenuBarButton(item: UIMenuItem, toolbar: UIMenuBar, menu: UIMenu, button_classes: UIClassesSpec | undefined, vertical: boolean): UIViewSpec {
    if(yResolve(item.when) === false || item.header != undefined)
        return $el.DIV('ui-menu-bar-placeholder');

    if(item.separator) {
        return $el.DIV('ui-menu-bar-separator');
    } else if(item.space != undefined) {
        return $el.S(item.space);
    } else if(item.inline) {
        return new UIMenuBar(item.inline, { kind: 'inline' });
    } else if(item.widget) {
        return item.widget();
    } else if(item.select) {
        const select = new UISelect(item.select, { kind: 'k-toolbar', classes: 'm-hug', no_focus: true, display: item.display });
        select.value = item.value;
        $on(select, 'value', v => {
            item.value = v;
            menu.update(item, toolbar);
            item.changed?.(item.value);
        });
        return select;
    } else {
        const button = new UIButton({ icon: item.icon, title: (item.icon && !vertical) ? undefined : yResolve(item.title), kind: 'k-menubar', classes: button_classes, link: item.link, prevent_blur: true });

        if(vertical)
            button.classList.add('m-vertical');

        if('checked' in item) {
            button.classList.add('m-check');
            button.classList.toggle('f-checked', item.checked);

            button.on_click = () => {
                item.checked = !item.checked;
                const new_value = item.checked;
                button.classList.toggle('f-checked', new_value);
                item.changed?.(new_value);
                menu.update(item, toolbar)
            }
        } else if(item.popup) {
            button.on_click = () => {
                const popup = item.popup();
                if(popup instanceof Promise) {
                    popup.then(p => {
                        uiShowPopup(p, button, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, can_flip: true });
                    });
                } else {
                    uiShowPopup(popup, button, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, can_flip: true });
                }
            }
        } else if(item.submenu) {
            button.on_click = () => {
                const submenu = yResolve(item.submenu);
                if(submenu instanceof Promise) {
                    submenu.then(submenu => { uiShowMenu(submenu, button) });
                } else {
                    uiShowMenu(submenu, button);
                }
            }
        } else {
            button.on_click = () => {
                item.action?.();
                menu.on_option?.(item);
                toolbar.onOptionClicked();
            }
        }

        if(item.highlight?.())
            button.classList.add('f-highlighted');

        return button;
    }
}

export class UIMenuBar extends UIView {
    private _menu?: UIMenu;
    private _hamburger: UIButton;
    private _vertical: boolean;
    private _open: boolean;
    // private _map!: WeakMap<UIMenuItem, UIViewSpec>;
    private _button_classes: UIClassesSpec | undefined;

    constructor(menu?: UIMenu, options: UIMenuBarOptions = {}) {
        const kind = options.kind || 'primary';

        super('nav', ['ui-menu-bar k-' + kind, kind == 'primary' && 'c-dark', options.classes]);
        this._vertical = false;

        this._open = false;

        const hamburger = this._hamburger = new UIButton({
            kind: 'k-hamburger', on_click: () => {
                this._open = !this._open;
                if(this._open) {
                    hamburger.checked = true;
                    this.classList.add('f-open');
                } else {
                    hamburger.checked = false;
                    this.classList.remove('f-open');
                }
            }
        });

        this.root.addEventListener('click', e => {
            if(this._open && !this._vertical && !hamburger.ui_elem.contains(e.target as Node)) {
                this._open = false;
                hamburger.checked = false;
                this.classList.remove('f-open');
            }
        });

        if(menu) {
            this._menu = menu;
            this._render();
        }
    }

    private _render() {
        const map = /*this._map =*/ new WeakMap();
        const menu = this._menu;

        this.ui_empty();

        if(menu && !menu.hidden) {
            for(const item of menu.items) {
                const view = makeMenuBarButton(item, this, menu, this._button_classes, this._vertical);
                map.set(item, view);
                this.ui_add(view);
            }
        }

        this.ui_add(this._hamburger);

        // if(menu.flags! & UIMenuFlags.Steps)
        //     this.classList.add('f-steps');
    }

    // private _update(item: UIMenuItem) {
    //     const map = this._map;
    //     const old = map.get(item);
    //     if(!old) return;

    //     const view = makeMenuBarButton(item, this, this._menu!, this._button_classes, this._vertical);
    //     old.ui_replace(view);
    //     map.set(item, view);
    // }

    set button_classes(classes: UIClassesSpec | undefined) {
        this._button_classes = classes;
    }

    set menu(menu: UIMenu) {
        this._menu = menu;
        this._render();
    }

    set vertical(v: boolean) {
        if(v == this._vertical) return;

        if(v) {
            this.classList.add('m-vertical');
            this.classList.remove('c-dark');
        } else {
            this.classList.remove('m-vertical');
        }

        this._vertical = v;
    }

    get strut(): UIRect {
        if(this.root.ui_hidden) {
            return [0, 0, 0, 0];
        } else if(this._vertical) {
            return [264, 0, 0, 0];
        } else {
            return [0, 54, 0, 0];
        }
    }

    get mobile_strut(): UIRect {
        if(this.root.ui_hidden) {
            return [0, 0, 0, 0];
        } else if(this._vertical) {
            return [0, 0, 0, 0];
        } else {
            return [0, 54, 0, 0];
        }
    }

    onOptionClicked() {
        if(this._open && this._vertical) {
            this._open = false;
            this._hamburger.checked = false;
            this.classList.remove('f-open');
        }
    }
}

// TODO: fix and test this
export class UIBottomBar extends UIView {
    _right?: HTMLElement;
    _left?: HTMLElement;
    _left_view?: UIView;
    _fields: HTMLElement;

    constructor() {
        super('nav', 'ui-bottom-bar');

        this.ui_add([$el.S(), this._fields = $el.DIV('r-fields'), $el.S()]);
    }

    setRight(title: string, on_click: () => void) {
        if(this._right)
            this._right.remove();
        const elem = this._right = $el.create('div', 'r-option', title);
        elem.addEventListener('click', on_click);
        this.ui_add(elem);
    }

    setLeft(title: string, on_click: () => void) {
        if(this._left)
            this._left.remove();
        const elem = this._left = $el.create('div', 'r-option', title);
        elem.addEventListener('click', on_click);
        this.ui_prepend(elem);
    }

    setFields(fields: UIViewSpec[]) {
        this._fields.ui_empty(fields);
    }

    setLeftView(view: UIView) {
        if(this._left_view)
            this._left_view.remove();
        this._left_view = view;
        this.ui_prepend(view);
    }

    set right_title(title: string) {
        if(this._right)
            this._right.textContent = title;
    }

    set left_title(title: string) {
        if(this._left)
            this._left.textContent = title;
    }
}

export class UIBreadCrumbs extends UIView {
    private _crumbs?: UIValue<number>[];

    on_option?: (k: number) => void;

    constructor() {
        super('nav', 'ui-bread-crumbs');
    }

    set crumbs(crumbs: UIValue<number>[] | undefined) {
        this._crumbs = crumbs;
        this._build();
    }

    private _build() {
        this.ui_empty();
        const crumbs = this._crumbs;
        if(!crumbs || !crumbs.length) return;

        if(crumbs.length >= 2) {
            const last = crumbs[crumbs.length - 2];
            const back = $el.create('div', 'r-option r-back');
            back.appendChild($el.ICON(UIIcon.ANGLE_LEFT));
            back.appendChild($el.SPAN('r-text', last.name ? `${$i18n.ui.back_to}\u00a0${last.name}` : "Back"));
            back.addEventListener('click', () => {
                this.on_option?.(last.key);
            });
            this.ui_add(back);
        }

        for(let i = 1; i < crumbs.length; i++) {
            let c = crumbs[i];
            if(i > 1)
                this.ui_add($el.create('div', 'r-separator'));
            const elem = $el.create('div', 'r-option r-crumb', c.name || i + '');
            elem.addEventListener('click', () => {
                this.on_option?.(c.key);
            });
            this.ui_add(elem);
        }
    }

    get strut(): UIRect {
        if(this.root.ui_hidden) {
            return [0, 0, 0, 0];
        } else {
            return [0, 54, 0, 0];
        }
    }

    get mobile_strut(): UIRect {
        if(this.root.ui_hidden) {
            return [0, 0, 0, 0];
        } else {
            return [0, 54, 0, 0];
        }
    }
}

export class UIMenuHeader extends UIView {
    // private _menu?: UIMenu;
    private _top: UIMenuBar;
    private _bottom: UIMenuBar;
    private _crumbs: UIBreadCrumbs;
    // private _options: UIMenuHeaderOptions;

    on_crumbs?: (k: number) => void;

    constructor() {
        super('header', 'ui-menu-header');
        this._top = new UIMenuBar();
        this._bottom = new UIMenuBar(undefined, { kind: 'secondary' });
        this._crumbs = new UIBreadCrumbs;
        this._crumbs.on_option = k => { this.on_crumbs && this.on_crumbs(k); };

        this._top.ui_hide();
        this._bottom.ui_hide();
        this._crumbs.ui_hide();
        this.ui_add([this._top, this._bottom, this._crumbs]);

        // this._options = {};
    }

    setBannerHeight(height: number) {
        this.root.style.paddingTop = `${height}px`;
    }

    set options(_options: UIMenuHeaderOptions) {
        // this._options = options;
    }

    setMenu(menu: UIMenu | undefined, options: UIMenuHeaderOptions = {}) {
        // if(menu === this._menu) return;
        // this._menu = menu;
        if(menu) {
            this._top.button_classes = options.button_classes;

            if(options.position == UIRelPos.Left) {
                this.classList.add('m-left');
                this._top.vertical = true;
            } else {
                this.classList.remove('m-left');
                this._top.vertical = false;
            }

            this._top.menu = menu;
            this._top.ui_show();

            const secondary = menu.secondary?.();
            if(secondary) {
                this._bottom.menu = secondary;
                this._bottom.ui_show();
            } else {
                this._bottom.ui_hide();
            }
        } else {
            this._top.ui_hide();
            this._bottom.ui_hide();
            this.classList.remove('m-left');
        }
    }

    setCrumbs(crumbs: UIValue<number>[] | undefined, options: UIMenuHeaderOptions = {}) {
        if(crumbs && !options.no_crumbs) {
            this._crumbs.crumbs = crumbs;
            this._crumbs.ui_show();
        } else {
            this._crumbs.ui_hide();
        }
    }

    get strut(): UIRect {
        const top = this._top.strut;
        const bottom = this._bottom.strut;
        const crumbs = this._crumbs.strut;

        return uiAddRects(top, bottom, crumbs);
    }

    get mobile_strut(): UIRect {
        const top = this._top.mobile_strut;
        const bottom = this._bottom.mobile_strut;
        const crumbs = this._crumbs.mobile_strut;

        return uiAddRects(top, bottom, crumbs);
    }
}
